import { BaseElementDefBase, BaseElementDef } from "./BaseElementDef";
import { combineLatest, from, Observable, ReplaySubject } from "rxjs";
import { FormItem } from "@cs/FormItem";
import { FormDefinitionItem } from "./__DefinitionDataInterface";
import { DataTableSelectedColumnDataSettings } from "@cs-ts/data-table-selected-column-data-settings";
import { DataTableSettings } from "@cs-ts/data-table-settings";
import { CalculationContext } from "./__CalculationContextInterface";
import { switchMap, map, first } from "rxjs/operators";
import { makeBackendFunc, ExtendedBackendResult } from "../../lib/calculations.direct";
import { KeyValue } from "@cs/KeyValue";

export interface FormDataTableItem extends FormItem {
    selectedRows: { data: string[] }[];
}
export interface FormDataTableItemInput extends FormItem {
    selectedRows: string[][];
}

export default class DataTableDef extends BaseElementDef<DataTableSettings, FormDefinitionItem<DataTableSettings>, FormItem> {
    declare definition: FormDefinitionItem<DataTableSettings>;
    declare selectedRows: string[][] | null;
    declare selectedRows$: ReplaySubject<string[][]>;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormItem, definitionData: FormDefinitionItem<DataTableSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFDataTable"; // TODO - not hardcoded?
    }

    setData(data: FormDataTableItemInput, excludeChildren: boolean) {
        super.setData(data, excludeChildren);
        this.setKey(data, "selectedRows", "selectedRows");
        if (this.selectedRows == null) {
            this.setSelectedRows([]);
        }
    }

    setSelectedRows(rows: string[][]) {
        this.selectedRows = rows;
        this.selectedRows$.next(rows);
    }

    getSelectedRows() {
        return this.selectedRows;
    }

    getData(excludeChildren: boolean) {
        const result = super.getData(excludeChildren) as FormDataTableItem;
        if (result != null && this.selectedRows != null) {
            result.selectedRows = this.selectedRows.map(v => ({ data: v }));
        }
        return result;
    }

    async exportRows(exportRowsData: KeyValue[][], exportType: string) {
        type ExportData = { data: KeyValue[][] | null; dataCount: number | null; exportType: string };
        const exportsAll = exportType == "Export All";
        let result: ExportData = {
            exportType,
            data: exportsAll ? null : exportRowsData,
            dataCount: exportsAll ? null : exportRowsData?.length,
        };
        if (this.definition.settings.dataSourceCalculationId != null && exportsAll) {
            const func = makeBackendFunc("exportDataTableDataCalc");
            const calc = this.definition.settings.calculations[this.definition.settings.dataSourceCalculationId][0];
            const otherData$ = from([{ data: null, dataCount: null, exportType }]);
            const exportRows$ = (func(calc, { node: this, rootCalc: calc, ...this.calculationContext, debug: false }, otherData$, null) as ExtendedBackendResult).result as Observable<{ result: ExportData }>;
            const exportRows = await exportRows$.pipe(first()).toPromise();
            result = exportRows!.result;
        }
        return (await this.invokeAsync("exportDataTableData", JSON.stringify(result))) as {
            Success: boolean;
            Token: string;
        };
    }

    rows$(skip$: Observable<number | null>, top$: Observable<number | null>, sort$: Observable<[string, string][]>) {
        if (this.definition.settings.dataSourceCalculationId != null) {
            const func = makeBackendFunc("resolveDatasourceLookup");
            const calc = this.definition.settings.calculations[this.definition.settings.dataSourceCalculationId][0];
            ////const rows$ = func(calc, { node: this, ...this.calculationContext, debug: false }).result as Observable<{ result: Record<string, string>[] }>;
            ////return rows$.pipe(
            ////    switchMap(result => {
            ////        return combineLatest(skip$, top$, sort$).pipe(
            ////            map(([skip, top, sort]) => {
            ////                let filteredRows = (result.result ?? []).sort((a, b) => {
            ////                    let r = 0;
            ////                    for (const [sortCol, sortDir] of sort) {
            ////                        r = a[sortCol].localeCompare(b[sortCol]) * (sortDir == "asc" ? 1 : -1);
            ////                        if (r != 0) break;
            ////                    }
            ////                    return r;
            ////                });
            ////                if (skip != null && top != null) {
            ////                    filteredRows = filteredRows.slice(skip, skip + top);
            ////                } else if (skip != null) {
            ////                    filteredRows = filteredRows.slice(skip);
            ////                } else if (top != null) {
            ////                    filteredRows = filteredRows.slice(0, top);
            ////                }
            ////                return { data: filteredRows, dataCount: result.result?.length ?? 0 };
            ////            }),
            ////        );
            ////    }),
            ////);
            const otherData$ = combineLatest(skip$, top$, sort$).pipe(map(([skip, top, sort]) => ({ SkipRows: skip, OnlyFirstRows: top, Sort: sort })));
            const rows$ = (func(calc, { node: this, rootCalc: calc, ...this.calculationContext, debug: false }, otherData$, null) as ExtendedBackendResult).result as Observable<{ result: { pagedRows: Record<string, string>[]; unpagedRowCount: number }; otherData: { SkipRows: number; OnlyFirstRows: number; Sort: string[][] } }>;
            return rows$.pipe(
                map(({ result: data, otherData }) => {
                    return { data: data?.pagedRows ?? [], dataCount: data?.unpagedRowCount ?? 0 };
                }),
            );
        } else {
            return combineLatest(skip$, top$, sort$).pipe(
                switchMap(async ([skip, top, sort]) => {
                    const result = (await this.invokeAsync("resolveDatatableData", JSON.stringify({ skip, top, sort }))) as {
                        data: { Key: string; Value: string }[][];
                        dataCount: number;
                    };
                    return {
                        data: result.data.map(i =>
                            i.reduce(
                                (r, i) => {
                                    r[i.Key] = i.Value;
                                    return r;
                                },
                                {} as Record<string, string>,
                            ),
                        ),
                        dataCount: result.dataCount,
                    };
                }),
            );
        }
    }
}

export class DataTableSelectedColumnDataDef extends BaseElementDef<DataTableSelectedColumnDataSettings, FormDefinitionItem<DataTableSelectedColumnDataSettings>, FormItem> {
    declare definition: FormDefinitionItem<DataTableSelectedColumnDataSettings>;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormItem, definitionData: FormDefinitionItem<DataTableSelectedColumnDataSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFDataTableSelectedColumnData"; // TODO - not hardcoded?
    }

    valueList$(): Observable<string[]> {
        const datatable = this.parentElement as DataTableDef;
        const colIndex = datatable.definition.settings.tableColumns.findIndex(i => i.datasourceColumn == this.definition.settings.column);
        return datatable.selectedRows$.pipe(map(i => i.map(j => j[colIndex])));
    }

    effectiveValueList$(): Observable<string[]> {
        return this.valueList$();
    }
}
